/**
 * 今日の運動の強さの提案（睡眠プランの一部）
 *
 * 睡眠の足りなさ・HRV・安静時心拍・Body Battery・トレーニングレディネスから決めた強さを表示する。
 */

import { Dumbbell } from 'lucide-react'

import type { ExerciseAdvice, ExerciseLevel } from '@/api/types/sleep'
import { parseUtcDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'

const LEVEL_STYLE = new Map<ExerciseLevel, string>([
  ['hard', 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'],
  ['moderate', 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'],
  ['light', 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'],
  ['rest', 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200'],
])

const formatTime = (iso: string): string =>
  parseUtcDate(iso).toLocaleTimeString('ja-JP', { timeZone: 'Asia/Tokyo', hour: '2-digit', minute: '2-digit' })

type Props = {
  advice: ExerciseAdvice
  className?: string
}

export const ExerciseAdviceCard = ({ advice, className }: Props) => (
  <section className={cn('space-y-2 rounded-xl border bg-card p-4 shadow-sm', className)}>
    <div className="flex items-center gap-2">
      <Dumbbell className="size-4 text-muted-foreground" />
      <h3 className="text-sm font-semibold">今日の運動の強さ</h3>
      <span className={cn('ml-auto rounded-full px-2.5 py-0.5 text-xs font-medium', LEVEL_STYLE.get(advice.level))}>
        {advice.label}
      </span>
    </div>
    <p className="text-sm">{advice.summary}</p>
    {advice.hard_until && advice.level !== 'rest' && (
      <p className="text-xs text-muted-foreground">
        強めの運動は {formatTime(advice.hard_until)} までに終えると寝つきを邪魔しません
      </p>
    )}
    {advice.reasons.length > 0 && (
      <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
        {advice.reasons.map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
    )}
  </section>
)
